// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ActiveElement, ChartEvent } from "chart.js";
import { Chart } from "react-chartjs-2";
import { SankeyController, Flow } from "chartjs-chart-sankey";

import { IAction } from "actions";
import { isSet } from "shared/utils/utils";
import { getEnergySourceName } from "guiutils";

import TariffCircuitListModel from "model/tariffcircuitlist";
import ChartBaseComponent, {
  IChartBaseDispatch,
  IChartBaseExtProps,
  IChartBaseProps,
  IChartBaseState,
  mapStateToProps as baseMapStateToProps,
  mapDispatchToProps as baseMapDispatchToProps,
  IChartTariffNode,
} from "./ChartBase";
import { IState } from "states/states";
import cssContent from "constants.module.scss";
import { HierarchySelectionState } from "./TreeViewDropDown";

ChartJS.register(SankeyController, Flow);

interface ISankeyChartExtProps extends IChartBaseExtProps {}

interface ISankeyChartProps extends IChartBaseProps {}

interface ISankeyChartState extends IChartBaseState {}

interface ISankeyChartDispatch extends IChartBaseDispatch {}

// extension to SankeyDataPoint --> but this type is not marked as export -> so we copy it!
interface ISankeyData {
  from: string;
  to: string;
  flow: number;
  fromColor: string;
  toColor: string;
  curSource: string;
  tooltip: string;
  label: string;
  tariffNodeRef: IChartTariffNode;
}

const mapStateToProps = (state: IState, ownProps: IChartBaseExtProps) => {
  const props = {
    ...baseMapStateToProps(state, ownProps),
  };

  return props;
};

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISankeyChartDispatch = {
    ...baseMapDispatchToProps(dispatch),
  };

  return dispatchProps;
};

ChartJS.register(ArcElement, Tooltip, Legend);

class SankeyChartComponent extends ChartBaseComponent<
  ISankeyChartProps & ISankeyChartExtProps & ISankeyChartDispatch,
  ISankeyChartState
> {
  // enable/disable gray-out of items based on hierarchy selection
  private readonly isHierarchyGrayOutEnabled = false;

  private getChartData() {
    const allChartData: ISankeyData[][] = [];

    // prepare now all sankey-data with the given list!
    const selectedTariffs = this.getSelectedTariffCircuits(
      this.props.isHierarchyViewMode === false, // show all tariffs when not in hierarchy mode,
      true, // show all categories in color
      false, // only color selected tariffs, not the whole hierarchy
    ).selectedTariffs;

    // get the minimum-value available (show all values below the minimum values -> 0-values -> as min-value)
    // --> better visibility on sankey-chart
    const minValue = selectedTariffs.reduce((prev, tariff) => {
      const value = Math.abs(tariff.value ?? 0);
      return prev > value && value !== 0 ? value : prev;
    }, 0xfffffff);

    // map the data now down to the relevant information for the sankey-chart
    const chartData = selectedTariffs.map((curTariff) => {
      const parentTariffOptionsEntry = selectedTariffs.find(
        (entry) => entry.id === curTariff?.parentId,
      );
      const parentTariffCircuit = this.props.tariffCircuits.find(
        (curItem, curIndex) => (curItem.id ?? curIndex) === curTariff?.parentId,
      );
      const currentName = curTariff.name;
      const parentName = parentTariffCircuit?.name ?? `${currentName}: `;
      const value = curTariff.value;

      const fromLabel =
        isSet(value) && value < 0 ?
          curTariff.valueText
        : (parentTariffOptionsEntry?.valueText ?? curTariff.valueText);
      const toLabel =
        isSet(value) && value < 0 ?
          (parentTariffOptionsEntry?.valueText ?? curTariff.valueText)
        : curTariff.valueText;

      const isFilterEnabled = isSet(this.props.filterText) && this.props.filterText !== "";
      const isHighlighted =
        !isFilterEnabled ||
        curTariff.name.toLocaleLowerCase().includes(this.props.filterText.toLocaleLowerCase());

      const isHighlightedParent =
        !isFilterEnabled ||
        parentTariffOptionsEntry?.name
          .toLocaleLowerCase()
          .includes(this.props.filterText.toLocaleLowerCase());

      const parentColor =
        (
          curTariff?.isVisible === true &&
          (curTariff?.isDisabled === false || !this.isHierarchyGrayOutEnabled) &&
          isHighlightedParent &&
          parentTariffOptionsEntry?.color
        ) ?
          parentTariffOptionsEntry.color
        : cssContent.GRAPH_GRAYED_OUT;
      const currentColor =
        (
          curTariff?.isVisible === true &&
          (curTariff?.isDisabled === false || !this.isHierarchyGrayOutEnabled) &&
          isHighlighted
        ) ?
          curTariff.color
        : cssContent.GRAPH_GRAYED_OUT;

      const fromName = `${isSet(value) && value < 0 ? currentName : parentName}`;
      const toName = `${isSet(value) && value < 0 ? parentName : currentName}`;
      const from = `${fromName}: ${fromLabel}`;
      const to = `${toName}: ${toLabel}`;
      const fromColor = isSet(value) && value < 0 ? currentColor : parentColor;
      const toColor = isSet(value) && value < 0 ? parentColor : currentColor;
      const flow = Math.abs(value ?? 0) === 0 ? minValue : Math.abs(value!);
      return {
        from,
        to,
        flow,
        fromColor,
        toColor,
        curSource: this.props.selectedEnergySource,
        tooltip: `${fromName} -> ${toName}: ${curTariff.valueText}`,
        label: to,
        tariffNodeRef: curTariff,
      } as ISankeyData;
    });

    allChartData.push(chartData);

    return allChartData;
  }

  public renderSankeys() {
    // get the chart-data for the sankey-chart...
    const activeTarifffNodes = this.getChartData();
    const activeRootTarrifId =
      this.props.isHierarchyViewMode === true ?
        this.allTariffCircuits.find((tl) => tl.checked === HierarchySelectionState.SELECTED)
          ?.referenceEntry?.id
      : undefined;
    // render the prepared TariffNodes
    // map the node-data now to a React-Component
    return (
      <>
        {activeTarifffNodes.map((anEnergyNodeInfo, eIndex) => {
          const values: any = {};
          anEnergyNodeInfo.forEach((nodeInfo) => {
            values[nodeInfo.to] = nodeInfo.label;
          });

          return (
            anEnergyNodeInfo.length > 0 && (
              <React.Fragment key={`dk_${anEnergyNodeInfo.length}_${eIndex}`}>
                {activeTarifffNodes.length > 1 && anEnergyNodeInfo.length > 0 && (
                  <h2 className="sourceheading">
                    {getEnergySourceName(anEnergyNodeInfo[0].curSource)}
                  </h2>
                )}
                <div id="sankey" className={"sankey"}>
                  <Chart
                    type="sankey"
                    height={this.getHeight(anEnergyNodeInfo.length)}
                    width={"100%"}
                    data={{
                      datasets: [
                        {
                          data: anEnergyNodeInfo,
                          colorFrom: (data: any) => data.raw?.fromColor as string,
                          colorTo: (data: any) => data.raw?.toColor as string,
                          nodeWidth: 5,
                          borderWidth: 0,
                          size: "max",
                          labels: values,
                        },
                      ],
                    }}
                    options={{
                      animation: false,

                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          enabled: true,
                          bodyColor: "#aaaaaa",
                          backgroundColor: "#444444",
                          displayColors: true,
                          callbacks: {
                            label: (tooltipItem) => {
                              return anEnergyNodeInfo[tooltipItem.dataIndex].tooltip;
                            },
                          },
                        },
                        datalabels: {
                          display: false,
                        },
                      },
                      onClick:
                        this.isHierarchyGrayOutEnabled ?
                          (event, elements, chart) =>
                            this.onChartClick(event, elements, anEnergyNodeInfo, activeRootTarrifId)
                        : undefined,
                      onHover: (event, elements, chart) =>
                        this.checkMouse(elements, anEnergyNodeInfo, this.isHierarchyGrayOutEnabled),
                    }}
                  />
                </div>
              </React.Fragment>
            )
          );
        })}
      </>
    );
  }

  public render() {
    // must be called in every derrived class from ChartBase!!!
    // the field allTariffCircuits is getting prepared within that function
    this.prepareHierarchyData();

    return this.renderSankeys();
  }

  private onChartClick(
    event: ChartEvent,
    elements: ActiveElement[],
    anEnergyNodeInfo: ISankeyData[],
    inRootId?: number,
  ) {
    // tariff clicked -> use as new root
    const clickedEnergyNodeInfoIndex = elements.length ? elements[0].index : undefined;

    if (isSet(clickedEnergyNodeInfoIndex)) {
      const clickedTariffIdIdx = anEnergyNodeInfo[clickedEnergyNodeInfoIndex].tariffNodeRef.id;

      const tcModel = new TariffCircuitListModel(
        this.props.tariffCircuits,
        this.props.energyMeters,
      );

      const newSelectedTcIds = () => {
        if (this.props.isHierarchyViewMode === true) {
          return inRootId === clickedTariffIdIdx ?
              //select parent
              tcModel.updateHierarchySelection(
                this.allTariffCircuits,
                [clickedTariffIdIdx],
                undefined,
              )
              // select this tariff as new root
            : this.prepareNextLevel(tcModel, clickedTariffIdIdx);
        }

        const allCurrentIds =
          isSet(this.props.selection?.tariffCircuitIds) ?
            this.props.selection?.tariffCircuitIds
          : this.props.tariffCircuits.map((item, idx) => item.id ?? idx);

        const childIds =
          isSet(allCurrentIds.find((tcId) => tcId === clickedTariffIdIdx)) ?
            (allCurrentIds.filter((tcId) => clickedTariffIdIdx !== tcId) as number[])
          : (allCurrentIds.concat([clickedTariffIdIdx]) as number[]);

        return childIds;
      };

      this.props.onHierarchyChanged(newSelectedTcIds(), this.props.isHierarchyViewMode ?? true);
    }
  }

  private checkMouse(
    elements: ActiveElement[],
    anEnergyNodeInfo: ISankeyData[],
    isHierarchyGrayOutEnabled: boolean,
  ) {
    // tariff hovered -> show different mouse-pointer
    const clickedEnergyNodeInfoIndex = elements.length ? elements[0].index : undefined;
    const isClickable =
      this.props.isHierarchyViewMode === false ||
      (isSet(clickedEnergyNodeInfoIndex) &&
        this.props.tariffCircuits.some(
          (tariff) =>
            tariff.parent === anEnergyNodeInfo[clickedEnergyNodeInfoIndex].tariffNodeRef?.id,
        ));
    const element = document.getElementById("sankey")?.getElementsByTagName("canvas")[0];
    if (isSet(element)) {
      element.style.cursor = isClickable && isHierarchyGrayOutEnabled ? "pointer" : "default";
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SankeyChartComponent);
